import * as React from "react";
import styled, { css } from "styled-components";

import { Button } from "src/library/components/Button";
import HtmlBody from "src/library/components/Notes/HtmlBody";
import NotificationInfoSidebar from "src/core/notifications/components/NotificationInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  INotificationReadingInfo,
  useNotificationsApi
} from "src/core/notifications/api/notifications.api";
import useNotification from "src/core/notifications/hooks/useNotification";
import { PageProps } from "src/pages/Router/routes";

const NotificationDetailsPage: React.ReactFCWithChildren<PageProps<unknown, {notificationId: string;}>> = props => {
  const {
    match, history, terminology
  } = props;

  const notificationId = match ? parseInt(match.params.notificationId, 0) : 0;
  const [ notificationReadingInfoResponse, getNotificationReadingInfo ] = useNotificationsApi("GET_NOTIFICATION_READING_INFO");
  const { isLoading: isLoadingNotification, notification } = useNotification(notificationId);
  const [ notificationReadingInformation, setNotificationReadingInformation ] = React.useState<INotificationReadingInfo | undefined>(undefined);
  const [ fetchingNotificationReadingInformation, setFetchingNotificationReadingInformation ] = React.useState<boolean>(false);

  // Get notification reading information.
  React.useEffect(() => {
    setFetchingNotificationReadingInformation(true);
    getNotificationReadingInfo(notificationId);
  }, [ getNotificationReadingInfo, notificationId ]);

  // Set notification reading information.
  React.useEffect(() => {
    if (notificationReadingInfoResponse && notificationReadingInfoResponse.data) {
      setNotificationReadingInformation(notificationReadingInfoResponse.data);
    }

    setFetchingNotificationReadingInformation(false);
  }, [ notificationReadingInfoResponse ]);

  return (
    <Layout
      {...props}
      title={notification ? `${notification.title} - Details` : ""}
    >
      <MainContentWrapper fill>
        {!isLoadingNotification && notification && (
          <NotificationContainer>
            <h2>
              {notification.title}
            </h2>

            <HtmlBody body={notification.body} />
          </NotificationContainer>
        )}
      </MainContentWrapper>

      <InfoBarWrapper>
        <NotificationInfoSidebar
          terminology={terminology}
          notification={notification}
          readingInformation={notificationReadingInformation}
          isLoading={isLoadingNotification || fetchingNotificationReadingInformation || notificationReadingInfoResponse.pending}
          primaryAction={(
            <Button
              $buttonType="primary"
              background="lightGrey"
              onClick={() => history.goBack()}
            >
              Go back
            </Button>
          )}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const NotificationContainer = styled.div`
  max-width: 1000px;
  ${({ theme }) => css`
    background: ${theme.colors.white};
    padding: ${theme.spacing.padding.lg};
    span {
      display: block;
      padding: ${theme.spacing.padding.md};
    }
    h2 {
      display: block;
      padding: ${theme.spacing.padding.md} 0;
      font-size: ${theme.fonts.sizes.h2};
    }
  `};
`;

export default NotificationDetailsPage;
